import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Divider, useTheme } from '@mui/material';
import { ErrorIcon, SaveIcon } from '@sharedCR/coreDesignSystem/icons';
import { checkIsInternalCompany } from 'app/auth/authUtils';
import { FORMS_ROUTING_PARAMS, FormsPaths } from 'app/configs/routesConfig/forms';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import Skeleton from 'app/shared/components/common/Skeleton';
import ControlPanel from 'app/shared/components/document/DocumentView/ControlPanel';
import { getStyles as getDocumentViewStyles } from 'app/shared/components/document/DocumentView/DocumentView.styles';
import PdfCard from 'app/shared/components/document/DocumentView/PdfCard';
import ThumbnailControlPanel from 'app/shared/components/document/DocumentView/ThumbnailControlPanel';
import { FormEditorHeader } from 'app/shared/components/forms/FormEditor';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  DOCUMENT_DEFAULT_SCALE,
  DOCUMENT_THUMBNAILS_BLOCK_WIDTH_CLOSED,
  DOCUMENT_THUMBNAILS_BLOCK_WIDTH_OPEN,
  SCALES,
} from 'constants/documents';
import { FeatureFlag } from 'constants/featureFlags';
import {
  FieldType,
  FormOverrideTemplateType,
  FormTemplateTypeNewApi,
  FormViewerNavigationType,
  NavigationStateType,
} from 'constants/forms';
import { SKELETON_TYPES } from 'constants/skeleton';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { getScrollStyles } from 'styles/scrollbar.styles';
import { getDocumentInitialScale } from 'utils/documents';
import { getIsFormNonOverridable } from 'utils/forms';
import { jsonStringify } from 'utils/json';
import { logger } from 'utils/logger';
import { getPdfJsDocument } from 'utils/pdf';

import { getIsFromInternalCompany } from 'store/auth/selectors';
import {
  createFormOverrideAction,
  createTemplateElementAction,
  resetPdfMetadataAction,
  setFormNavigationStateTypeAction,
  updateFormTemplateAction,
} from 'store/forms/actions';
import {
  selectFormNavigationStateType,
  selectFormNavigationType,
  selectIsElementsChanged,
  selectPreviewingFormTemplate,
} from 'store/forms/selectors';
import { showErrorMessageAction } from 'store/global/actions';

import FormChangeHistoryButton from '../FormChangeHistory/FormChangeHistoryButton';
import { FormsTestIds } from '../Forms.testIds';

import AddDynamicFieldButton from './AddDynamicFieldButton';
import PdfCardWithElements from './PdfCardWithElements';
import { getStyles as getPdfEditorStyles } from './PdfEditor.styles';

const PdfEditor = ({
  header,
  formActions: viewFormActions,
  form,
  isLoading,
  setIsLoading,
  initialSelectedPage,
}) => {
  const { push, location } = useHistory();
  const dispatch = useDispatch();

  const pathParams = useParams();
  const loanId = useLoanId();
  const formParams = { selectedPage: initialSelectedPage };

  const isOverrideEnabled = useFeatureToggle(FeatureFlag.EnableFormLocalOverrides);
  const isInternalCompany = useSelector(getIsFromInternalCompany);
  const isOverridable =
    isOverrideEnabled &&
    !isInternalCompany &&
    checkIsInternalCompany({ companyId: form.companyId }) &&
    !form.hasLocalOverride &&
    !getIsFormNonOverridable({ formType: form.formType, isCustom: form.isCustom });

  const navigationType = useSelector(selectFormNavigationType);
  const isNavigationRequired = navigationType === FormViewerNavigationType.URL;
  const isNavigationNotRequired = navigationType === FormViewerNavigationType.STATE;

  const { historyEntityId, coborrowersNumber, nonborrowersNumber, formId, ...params } =
    isNavigationNotRequired ? formParams : pathParams;

  const navigationStateType = useSelector(selectFormNavigationStateType);
  const isElementsChanged = useSelector(selectIsElementsChanged);
  const previewPdfForm = useSelector(selectPreviewingFormTemplate);

  // TODO: 4146 US - figure out how to solve it without `pdfCopy` - using just 1 pdf version
  // `pdfCopy` - is copy of regular pdf (it is needed because 2 same pdf pages are rendered at the same time and it causes incorrect work of PDFJS lib)
  const [pdf, setPdf] = useState(null);
  const [pdfCopy, setPdfCopy] = useState(null);

  const pdfPagesWrapperRef = useRef(null);

  const [isPagesListOpen, setIsPagesListOpen] = useState(true);
  const [scale, setScale] = useState(DOCUMENT_DEFAULT_SCALE);

  const [page, setPage] = useState(parseInt(params.selectedPage));
  const selectedPage = isNavigationRequired ? parseInt(params.selectedPage) : page;

  const elements = _.get(form.pdfMetadata, 'elements', []);
  const groups = _.get(form.pdfMetadata, 'groups', []);
  const dependenceGroups = _.get(form.pdfMetadata, 'dependenceGroups', []);
  const editFormPath = FormsPaths.editForm.generatePath({
    [FORMS_ROUTING_PARAMS.formId]: form.formId,
    [FORMS_ROUTING_PARAMS.area]: form.state,
    [FORMS_ROUTING_PARAMS.formType]: form.formType,
    [FORMS_ROUTING_PARAMS.selectedPage]: selectedPage,
  });

  const isEditNavigationState = navigationStateType === NavigationStateType.EDIT;
  const isEditFormPath = isNavigationRequired
    ? location.pathname.includes(editFormPath)
    : isEditNavigationState;

  const theme = useTheme();
  const scrollClasses = getScrollStyles(theme);
  const thumbnailsWidth = isPagesListOpen
    ? DOCUMENT_THUMBNAILS_BLOCK_WIDTH_OPEN
    : DOCUMENT_THUMBNAILS_BLOCK_WIDTH_CLOSED;
  const documentViewClasses = getDocumentViewStyles(theme, {
    additionalHeaderHeight: 63,
    thumbnailsWidth,
  });

  const pdfEditorStyles = getPdfEditorStyles(theme, { additionalHeaderHeight: 0 });
  const documentSlideSx = useMemo(
    () => ({ ...documentViewClasses.documentSlide, ...scrollClasses.scroll }),
    [documentViewClasses.documentSlide, scrollClasses.scroll],
  );
  const pageNumberSx = useMemo(
    () => ({
      ...documentViewClasses.pageNumber,
      ...(isNavigationNotRequired ? documentViewClasses.pageNumberState : {}),
    }),
    [documentViewClasses.pageNumber, documentViewClasses.pageNumberState, isNavigationNotRequired],
  );
  const documentViewScrollSx = useMemo(
    () => ({ ...scrollClasses.scroll, ...pdfEditorStyles.documentView }),
    [scrollClasses.scroll, pdfEditorStyles.documentView],
  );

  const numPages = _.get(pdf, 'numPages', 0);

  useEffectSafeStrictMode(() => {
    const init = async () => {
      const pdfLink = form.template;

      if (!pdfLink || _.isObject(pdfLink)) {
        return;
      }

      try {
        setIsLoading(true);

        const PDFDocumentLoadingTask = await getPdfJsDocument({ url: pdfLink });
        const [pdf] = await Promise.all([
          PDFDocumentLoadingTask.promise,
          getPdfJsDocument({ url: pdfLink })
            .then(copyLoadingTask => copyLoadingTask.promise)
            .then(pdf => setPdfCopy(pdf)),
        ]);

        setPdf(pdf);

        const scale = await getDocumentInitialScale({
          pdf,
          containerRef: pdfPagesWrapperRef,
          isTurnedOn: true,
        });

        setScale(scale);
      } catch (err) {
        dispatch(showErrorMessageAction({ message: 'Could not get PDF', err }));
        logger.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [setIsLoading, previewPdfForm, dispatch, form.template]);

  useEffect(() => {
    return () => dispatch(resetPdfMetadataAction());
  }, [dispatch]);

  const onTogglePages = useCallback(() => setIsPagesListOpen(prev => !prev), []);

  const defaultNavigationType = isEditFormPath ? 'editForm' : 'viewForm';

  const onNavigate = useCallback(
    (selectedPage, navigationType = defaultNavigationType) => {
      if (isNavigationRequired) {
        FormsPaths[navigationType].navigateWith(push, {
          [FORMS_ROUTING_PARAMS.formId]: form.formId,
          [FORMS_ROUTING_PARAMS.area]: form.state,
          [FORMS_ROUTING_PARAMS.formType]: form.formType,
          [FORMS_ROUTING_PARAMS.loanId]: loanId,
          [FORMS_ROUTING_PARAMS.selectedPage]: selectedPage,
          [FORMS_ROUTING_PARAMS.historyEntityId]: historyEntityId,
          [FORMS_ROUTING_PARAMS.coborrowersNumber]: coborrowersNumber,
          [FORMS_ROUTING_PARAMS.nonborrowersNumber]: nonborrowersNumber,
        });
      }

      if (!isNavigationRequired) {
        setPage(selectedPage);
      }
    },
    [
      defaultNavigationType,
      isNavigationRequired,
      push,
      form.formId,
      form.state,
      form.formType,
      loanId,
      historyEntityId,
      coborrowersNumber,
      nonborrowersNumber,
    ],
  );

  const onNextPage = useCallback(() => {
    const action = isNavigationRequired ? onNavigate : setPage;

    action(selectedPage + 1);
  }, [isNavigationRequired, onNavigate, selectedPage]);

  const onPrevPage = useCallback(() => {
    const action = isNavigationRequired ? onNavigate : setPage;

    action(selectedPage - 1);
  }, [isNavigationRequired, onNavigate, selectedPage]);

  const onCancelClick = useCallback(() => {
    if (isNavigationRequired) {
      onNavigate(selectedPage, 'viewForm');

      return;
    }

    dispatch(resetPdfMetadataAction());
    dispatch(setFormNavigationStateTypeAction(NavigationStateType.VIEW));
  }, [dispatch, isNavigationRequired, onNavigate, selectedPage]);

  const updateForm = useCallback(() => {
    const dataToSend = {
      metadata: jsonStringify({ elements, groups, dependenceGroups }),
      formId: form.formId,
      type: FormTemplateTypeNewApi.PDF,
    };

    return dispatch(updateFormTemplateAction(dataToSend)).then(onCancelClick);
  }, [elements, groups, dependenceGroups, form.formId, dispatch, onCancelClick]);

  const overrideForm = useCallback(() => {
    const formData = new FormData();
    formData.append(
      'body',
      jsonStringify({
        formId: form.formId,
        formOverrideTemplate: { type: FormOverrideTemplateType.EXISTS_FORM_CONTENT },
        withPageNumbers: form.withPageNumbers,
      }),
    );

    return dispatch(createFormOverrideAction({ formId: form.formId, formData })).then(updateForm);
  }, [dispatch, form.formId, form.withPageNumbers, updateForm]);

  const onSaveClick = useCallback(() => {
    setIsLoading(true);

    const saveForm = isOverridable ? overrideForm : updateForm;

    saveForm()
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [setIsLoading, isOverridable, overrideForm, updateForm]);

  const onAddDynamicField = useCallback(() => {
    dispatch(createTemplateElementAction({ selectedPage, fieldType: FieldType.DYNAMIC }));
  }, [dispatch, selectedPage]);

  const onAddSigningTag = useCallback(() => {
    dispatch(createTemplateElementAction({ selectedPage, fieldType: FieldType.SIGNING }));
  }, [dispatch, selectedPage]);

  const editFormActions = useMemo(() => {
    const buttons = [
      {
        disabled: isLoading || !isElementsChanged,
        icon: SaveIcon,
        testName: 'save',
        title: 'Save form content',
        onClick: onSaveClick,
      },
      {
        disabled: isLoading,
        icon: ErrorIcon,
        testName: 'cancel',
        title: 'Cancel editing',
        onClick: onCancelClick,
        disableRipple: true,
      },
    ];

    return (
      <>
        <FormChangeHistoryButton
          formId={form.formId}
          formName={form.name}
          isGlobalForm={form.isGlobal}
        />
        {buttons.map(({ testName, ...props }) => (
          <ButtonWithTooltip
            key={props.title}
            {...props}
            data-testid={FormsTestIds.getPdfEditorActionButtonId(testName)}
          />
        ))}
      </>
    );
  }, [
    isLoading,
    onCancelClick,
    onSaveClick,
    isElementsChanged,
    form.formId,
    form.name,
    form.isGlobal,
  ]);

  const onZoomIn = useCallback(e => {
    e.preventDefault();

    setScale(prevState => {
      if (prevState >= _.last(SCALES)) {
        return prevState;
      }

      const currentScaleIndex = _.findIndex(SCALES, scale => scale === prevState);

      return SCALES[currentScaleIndex + 1];
    });
  }, []);

  const onZoomOut = useCallback(e => {
    e.preventDefault();

    setScale(prevState => {
      if (prevState <= _.first(SCALES)) {
        return prevState;
      }

      const currentScaleIndex = _.findIndex(SCALES, scale => scale === prevState);

      return SCALES[currentScaleIndex - 1];
    });
  }, []);

  const onSelectScale = useCallback(e => {
    e.preventDefault();
    setScale(e.target.value);
  }, []);

  const onCardClick = useCallback(({ currentTarget }) => {
    setPage(parseInt(currentTarget.dataset.page));
  }, []);

  return (
    <div className="flex flex-col h-full">
      <FormEditorHeader
        form={form}
        formActions={isEditFormPath ? editFormActions : viewFormActions}
        header={header}
        isEditMode={isEditFormPath}
      />

      <Divider variant="fullWidth" />

      <Box className="flex flex-1 min-h-0" sx={documentViewClasses.documentViewWrapper}>
        <Box className="flex flex-col" sx={documentViewClasses.documentSlideContainer}>
          <ThumbnailControlPanel
            disabledSplit
            isPagesList={isPagesListOpen}
            onToggle={onTogglePages}
          />

          {isLoading && (
            <Skeleton height={265} length={3} type={SKELETON_TYPES.PAPER_SMALL} width={240} />
          )}

          {!isLoading && isPagesListOpen && (
            <Box className="overflow-y-auto overflow-x-hidden" sx={documentSlideSx}>
              {Array.from({ length: numPages }).map((_, index) => {
                const currentPage = index + 1;

                const to = FormsPaths[defaultNavigationType].generatePath({
                  [FORMS_ROUTING_PARAMS.formId]: form.formId,
                  [FORMS_ROUTING_PARAMS.area]: form.state,
                  [FORMS_ROUTING_PARAMS.formType]: form.formType,
                  [FORMS_ROUTING_PARAMS.loanId]: loanId,
                  [FORMS_ROUTING_PARAMS.selectedPage]: currentPage,
                  [FORMS_ROUTING_PARAMS.historyEntityId]: historyEntityId,
                  [FORMS_ROUTING_PARAMS.coborrowersNumber]: coborrowersNumber,
                  [FORMS_ROUTING_PARAMS.nonborrowersNumber]: nonborrowersNumber,
                });

                const card = (
                  <>
                    <PdfCard
                      isThumbnail
                      isSelected={selectedPage === currentPage}
                      num={currentPage}
                      pdf={pdf}
                      scale={0.3}
                    />
                    <Box sx={pageNumberSx}>{currentPage}</Box>
                  </>
                );

                return isNavigationRequired ? (
                  <Box component={Link} key={index} sx={documentViewClasses.thumbnailCard} to={to}>
                    {card}
                  </Box>
                ) : (
                  <Box
                    data-page={currentPage}
                    sx={documentViewClasses.thumbnailCard}
                    onClick={onCardClick}
                  >
                    {card}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box className="flex flex-col" sx={pdfEditorStyles.documentViewContainer}>
          <Box
            className="flex items-center justify-between"
            sx={pdfEditorStyles.controlPanelWrapper}
          >
            <ControlPanel
              isRestrictedFunctionality
              pagesNumber={numPages}
              scale={scale}
              selectedPage={selectedPage}
              onNextPage={onNextPage}
              onPrevPage={onPrevPage}
              onSelectPage={onNavigate}
              onSelectScale={onSelectScale}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
            />

            {isEditFormPath && (
              <div className="flex items-center p-8">
                <AddDynamicFieldButton
                  isDisabled={isLoading}
                  selectedPage={selectedPage}
                  onAddDynamicField={onAddDynamicField}
                />

                <Button
                  color="primary"
                  data-testid={FormsTestIds.PDF_EDITOR_ADD_SIGN_TAG_BUTTON}
                  disabled={isLoading}
                  size="small"
                  variant="contained"
                  onClick={onAddSigningTag}
                >
                  Add Sign Tag
                </Button>
              </div>
            )}
          </Box>

          <Box className="overflow-y-auto" ref={pdfPagesWrapperRef} sx={documentViewScrollSx}>
            <div>
              {isLoading ? (
                <Skeleton height={800} length={1} type={SKELETON_TYPES.PAPER_HUGE} />
              ) : (
                <PdfCardWithElements
                  isEditFormPath={isEditFormPath}
                  pdf={pdfCopy}
                  scale={scale}
                  selectedPage={selectedPage}
                />
              )}
            </div>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

PdfEditor.propTypes = {
  header: PropTypes.element.isRequired,
  formActions: PropTypes.node.isRequired,
  form: PropTypes.object.isRequired,
  ...WithLoadingPropTypes,
};

export default withLoading(PdfEditor);
